import { randomUUID } from 'base/uuid';
import cloneDeep from 'lodash/cloneDeep';
import { Component, ComponentStyle, WfFlatNode } from './schema';

const partialDefaultStyle = ({
  name,
  styleLess,
  comb,
}: Pick<ComponentStyle, 'name' | 'styleLess' | 'comb'>): ComponentStyle => ({
  _id: randomUUID(),
  name,
  styleLess,
  comb,
  fake: false,
  type: 'class',
  namespace: '',
  children: [],
  variants: {},
});

export type PayloadQuery = {
  getRootNode: () => NodeQuery | null;
  ref: Component;
};

export type NodeQuery = {
  addStyle: (style: Pick<ComponentStyle, 'name' | 'styleLess'>) => void;
  removeLastStyle: () => void;
  children: () => NodeQuery[];
  styles: () => ComponentStyle[];
  walkForwardAll: () => Generator<NodeQuery>;
  ref: WfFlatNode;
};

export const pQuery = (component: Component): PayloadQuery => {
  // Create a copy of the payload upfront to prevent mutation of the original
  component = cloneDeep(component);

  const lazyNodesById = new Map<string, WfFlatNode>();
  const lazyStylesById = new Map<string, ComponentStyle>();

  const $Node = (node: WfFlatNode): NodeQuery => {
    const addStyle = ({
      name,
      styleLess,
    }: Pick<ComponentStyle, 'name' | 'styleLess'>) => {
      if (node.text != null) {
        return; // TODO: Try prevent this with types
      }

      const parentStyleId = node.classes[node.classes.length - 1];
      const parentStyle =
        parentStyleId == null
          ? null
          : component.payload.styles.find((s) => s._id === parentStyleId);

      const newStyle = partialDefaultStyle({
        name,
        styleLess,
        comb: parentStyle == null ? '' : '&',
      });

      if (parentStyle != null) {
        // Add it as a child of the parent style
        parentStyle.children.push(newStyle._id);
      }

      node.classes.push(newStyle._id);
      component.payload.styles.push(newStyle);
    };

    const removeLastStyle = () => {
      if (node.text != null) {
        return;
      }

      node.classes.pop();
    };

    function children() {
      const result = [];
      for (const child of node.children) {
        let childNode = lazyNodesById.get(child);
        if (!childNode) {
          for (const node of component.payload.nodes) {
            lazyNodesById.set(node._id, node);
            if (node._id === child) {
              childNode = node;
            }
          }

          if (childNode == null) {
            continue; // TODO: Warn on this maybe
          }
        }

        result.push($Node(childNode));
      }
      return result;
    }

    function styles(): ComponentStyle[] {
      const result: ComponentStyle[] = [];
      if (node.text != null) {
        return result;
      }

      for (const styleId of node.classes) {
        let style = lazyStylesById.get(styleId);
        if (!style) {
          for (const s of component.payload.styles) {
            lazyStylesById.set(s._id, s);
            if (s._id === styleId) {
              style = s;
              break;
            }
          }
          if (style == null) {
            continue; // TODO: Warn on this maybe
          }
        }
        result.push(style);
      }
      return result;
    }

    function* walkForwardAll(): Generator<ReturnType<typeof $Node>> {
      for (const $child of children()) {
        yield $child;

        yield* $child.walkForwardAll();
      }
    }

    return {
      addStyle,
      removeLastStyle,
      children,
      styles,
      walkForwardAll,
      ref: node,
    };
  };

  const getRootNode = (): NodeQuery | null => {
    // TODO: Maybe get the node that has no parents
    const node = component.payload.nodes[0];
    if (node.text) {
      return null;
    }
    return $Node(node);
  };

  return {
    getRootNode,
    ref: component,
  };
};
